/**
 * T-10: questions the coding agent asks on a task. The agent ends its turn with one fenced block,
 *
 * ```factory-questions
 * { "ask": "sam@example.com", "questions": [{ "id": "scope", "header": "Scope", "question": "…",
 *   "options": [{ "label": "Just the API", "description": "…" }], "multiSelect": false }] }
 * ```
 *
 * The hub posts its message on the task (the block shows as a question card), hands the task from the
 * AI builder to the person asked, and their answer goes back to the same T3 thread.
 *
 * SF-T-308: the one decisions block. `phase` says when it's for: `grill` (the default: questions before
 * building, as above) or `wrap` (the end of a turn: what to do next, read as a round of suggestions by
 * findFactorySuggestions, with options). Each item can carry `detail`, the `recommended` option, and for
 * wrap items `change`, `without` and `recommend` as suggestions do. A `factory-decisions` block (the name
 * the task first gave it) reads the same. A grill item's answer can be Skip (the agent's recommendation)
 * or Backlog (the agent makes a task of it for later), and Enough, build it ends the grilling.
 */

import { TASK_CHAT_MAX } from './types/tasks';
import { indentFollowingLines, readFollowingLines } from './list-item-lines';

export const FACTORY_QUESTIONS_LANG = 'factory-questions';
/** SF-T-308: another name for the same block, read as factory-questions. */
export const FACTORY_DECISIONS_LANG = 'factory-decisions';

/** SF-T-308: before building (questions) or at the end of a turn (what to do next). */
export type DecisionPhase = 'grill' | 'wrap';

/** Most questions in one round, options per question, and characters per piece of text. */
export const FACTORY_QUESTIONS_MAX = 8;
export const FACTORY_OPTIONS_MAX = 8;
const TEXT_MAX = 1000;

export interface FactoryQuestionOption {
  label: string;
  description?: string;
}

/** Characters in a wrap item's change or without, and in a round's progress line (SF-T-308). */
const NOTE_MAX = 500;
const DETAIL_MAX = 2000;

export interface FactoryQuestion {
  id: string;
  /** A short label, e.g. "Scope". */
  header?: string;
  question: string;
  /** SF-T-308: more on the question: why it matters, what's at stake. */
  detail?: string;
  /** None: answered in words. */
  options: FactoryQuestionOption[];
  multiSelect: boolean;
  /** SF-T-308: the label of the option the agent recommends (the one Skip means in grill). */
  recommended?: string;
  /** SF-T-308, wrap items: what building it would change, and what stays without it (as suggestions'). */
  change?: string;
  without?: string;
  /** SF-T-308, wrap items: the action the agent would pick, as written (yes, branch, backlog, skip). */
  recommend?: string;
  /**
   * SF-T-159: in a run that builds sub-tasks, the sub-task the question is about ("SF-T-42"), as the
   * agent wrote it. The hub asks it on that sub-task; without one, it's asked on the larger task.
   */
  task?: string;
}

export interface FactoryQuestions {
  /** Who should answer: an email or a name, as the agent wrote it. Null: the person who asked the AI builder. */
  ask: string | null;
  /** SF-T-308: grill (questions before building; also when it's left out) unless the block says wrap. */
  phase?: DecisionPhase;
  /** SF-T-308: how close the grilling is: what's still open (a grill round's line on the card). */
  progress?: string;
  questions: FactoryQuestion[];
}

/** SF-T-308: an option's label without the "(Recommended)" an agent may write in it. */
export const plainOptionLabel = (label: string) => label.replace(/\s*\(recommended\)\s*$/i, '').trim();

const text = (v: unknown, max = TEXT_MAX): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. */
export function parseFactoryQuestionsJson(raw: string): FactoryQuestions | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  // A bare list of questions is accepted too.
  const obj = (Array.isArray(data) ? { questions: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object') return null;
  // SF-T-308: `items` (or `decisions`) is the same list.
  const list = [obj.questions, obj.items, obj.decisions].find(Array.isArray) as unknown[] | undefined;
  if (!list) return null;
  const phase: DecisionPhase = typeof obj.phase === 'string' ? (obj.phase.trim().toLowerCase() === 'wrap' ? 'wrap' : 'grill') : looksLikeWrap(list) ? 'wrap' : 'grill';
  const questions: FactoryQuestion[] = [];
  // A wrap round holds as many as a suggestions block (FACTORY_SUGGESTIONS_MAX, 10).
  for (const [i, item] of list.slice(0, phase === 'wrap' ? WRAP_ITEMS_MAX : FACTORY_QUESTIONS_MAX).entries()) {
    const q = (typeof item === 'string' ? { question: item } : (item ?? {})) as Record<string, unknown>;
    // SF-T-308: a wrap item names itself with a title, as a suggestion does.
    const question = text(q.question) || text(q.title);
    if (!question) continue;
    const options = parseQuestionOptions(q.options);
    const header = text(q.header, 60);
    const task = text(q.task, 40);
    const detail = text(q.detail ?? q.description, DETAIL_MAX);
    const change = text(q.change, NOTE_MAX);
    const without = text(q.without, NOTE_MAX);
    const recommend = text(q.recommend, 40);
    const recommended = recommendedOption(q.recommended, options);
    questions.push({
      id: text(q.id, 60) || `q${i + 1}`,
      ...(header ? { header } : {}),
      question,
      ...(detail ? { detail } : {}),
      options,
      multiSelect: q.multiSelect === true,
      ...(recommended ? { recommended } : {}),
      ...(change ? { change } : {}),
      ...(without ? { without } : {}),
      ...(recommend ? { recommend } : {}),
      ...(task ? { task } : {}),
    });
  }
  if (!questions.length) return null;
  const progress = text(obj.progress, NOTE_MAX);
  return { ask: text(obj.ask, 200) || null, ...(phase === 'wrap' ? { phase } : {}), ...(progress ? { progress } : {}), questions };
}

/** An item's options, checked and trimmed (SF-T-308: a wrap item's too). */
export function parseQuestionOptions(raw: unknown): FactoryQuestionOption[] {
  return (Array.isArray(raw) ? raw : [])
    .slice(0, FACTORY_OPTIONS_MAX)
    .map((o): FactoryQuestionOption | null => {
      if (typeof o === 'string') return text(o, 200) ? { label: text(o, 200) } : null;
      const opt = (o ?? {}) as Record<string, unknown>;
      const label = text(opt.label, 200);
      if (!label) return null;
      const description = text(opt.description);
      return description ? { label, description } : { label };
    })
    .filter((o): o is FactoryQuestionOption => o !== null);
}

/** SF-T-308: most items in a wrap round, as in a factory-suggestions block. */
const WRAP_ITEMS_MAX = 10;

/**
 * SF-T-308: a block that leaves its phase out reads as wrap only when every item is plainly a suggestion: a title, no
 * question. Anything with a question stays grill, so a run that asks is never taken for one that's done.
 */
function looksLikeWrap(list: unknown[]): boolean {
  const items = list.filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === 'object');
  if (!items.length || items.length !== list.length) return false;
  const has = (q: Record<string, unknown>, k: string) => typeof q[k] === 'string' && (q[k] as string).trim() !== '';
  return items.every((q) => has(q, 'title') && !has(q, 'question'));
}

/**
 * SF-T-308: the recommended option's label: `recommended` as a label or an option's place (from 0), else the
 * first option whose label says "(Recommended)", as agents have written it. Undefined when there's none.
 */
export function recommendedOption(value: unknown, options: FactoryQuestionOption[]): string | undefined {
  if (typeof value === 'number' && Number.isInteger(value)) return options[value]?.label;
  if (typeof value === 'string' && value.trim()) {
    const want = plainOptionLabel(value).toLowerCase();
    const found = options.find((o) => plainOptionLabel(o.label).toLowerCase() === want);
    if (found) return found.label;
  }
  return options.find((o) => /\(recommended\)\s*$/i.test(o.label))?.label;
}

/**
 * The first fenced block in a message whose language is `lang` (```lang or ~~~lang), with where it
 * sits: its body, and the offsets of the whole block. Null when there's none. Also used for
 * factory-suggestions (T-14).
 */
export function findFencedBlock(message: string, lang: string): { body: string; start: number; end: number } | null {
  const body = message.replace(/\r\n?/g, '\n');
  const open = new RegExp('^ {0,3}(`{3,}|~{3,})[ \\t]*' + lang + '[ \\t]*$', 'm').exec(body);
  if (!open) return null;
  const fence = open[1];
  const bodyStart = open.index + open[0].length + 1;
  const close = new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}[ \\t]*$`, 'm').exec(body.slice(bodyStart));
  const bodyEnd = close ? bodyStart + close.index : body.length;
  return { body: body.slice(bodyStart, bodyEnd), start: open.index, end: close ? bodyStart + close.index + close[0].length : body.length };
}

/**
 * SF-T-308: the first decisions block in a message (factory-questions, or factory-decisions), either phase,
 * with where it sits; null when there's none (or it's broken).
 */
export function findDecisionsBlock(message: string): { questions: FactoryQuestions; start: number; end: number } | null {
  for (const lang of [FACTORY_QUESTIONS_LANG, FACTORY_DECISIONS_LANG]) {
    const block = findFencedBlock(message, lang);
    const questions = block ? parseFactoryQuestionsJson(block.body) : null;
    if (block && questions) return { questions, start: block.start, end: block.end };
  }
  return null;
}

/**
 * The message's questions: its decisions block in the grill phase, with where it sits; null when there's none
 * (or it's broken). SF-T-308: a block in the wrap phase is a round of suggestions (findFactorySuggestions), not questions.
 */
export function findFactoryQuestions(message: string): { questions: FactoryQuestions; start: number; end: number } | null {
  const found = findDecisionsBlock(message);
  return found && found.questions.phase !== 'wrap' ? found : null;
}

/** SF-T-159: the questions as a factory-questions block again, e.g. one sub-task's share of a round. */
export function formatFactoryQuestionsBlock(questions: FactoryQuestions): string {
  const json = JSON.stringify(
    {
      ...(questions.ask ? { ask: questions.ask } : {}),
      ...(questions.phase === 'wrap' ? { phase: 'wrap' } : {}),
      ...(questions.progress ? { progress: questions.progress } : {}),
      questions: questions.questions,
    },
    null,
    2
  );
  return '```' + FACTORY_QUESTIONS_LANG + '\n' + json + '\n```';
}

/** One question's answer: the options picked and anything written. */
export interface FactoryAnswer {
  picked: string[];
  other: string;
  /** SF-T-227: the person pressed Skip on this question; it goes to the agent as skipped. */
  skipped?: boolean;
  /** SF-T-308: the person pressed Backlog: not for this build; the agent makes a task of it (`other` is a note for it). */
  backlog?: boolean;
}

/** True when every question has an answer, or was skipped (SF-T-227) or sent to the Backlog (SF-T-308). */
export function answersComplete(questions: FactoryQuestion[], answers: Record<string, FactoryAnswer | undefined>): boolean {
  return questions.every((q) => {
    const a = answers[q.id];
    return Boolean(a && (a.skipped || a.backlog || a.picked.length || a.other.trim()));
  });
}

/**
 * SF-T-227: a skipped question's answer, as the answers message writes it and parseFactoryAnswers reads
 * it back, so the task's Decisions can say Skipped (isSkippedAnswer) rather than No answer.
 */
export const SKIPPED_ANSWER = '_(skipped)_';

/** How far an answer's further lines are indented: under the text after `   → ` (SF-T-265). */
const ANSWER_INDENT = 5;

/** True for a skipped question's answer from parseFactoryAnswers. */
export const isSkippedAnswer = (answer: string | null | undefined): boolean => answer === SKIPPED_ANSWER;

/** SF-T-308: a question sent to the Backlog, as the answers message writes it (with any note after it: `_(backlog)_: …`). */
export const BACKLOG_ANSWER = '_(backlog)_';

/** SF-T-308: for an answer from parseFactoryAnswers sent to the Backlog, its note ('' for none); null for any other answer. */
export function backlogAnswerNote(answer: string | null | undefined): string | null {
  if (!answer?.startsWith(BACKLOG_ANSWER)) return null;
  return answer.slice(BACKLOG_ANSWER.length).replace(/^:\s*/, '').trim();
}

/** SF-T-308: the line Enough, build it adds after the answers. */
export const ENOUGH_LINE =
  '**Enough questions: go on now.** Use your recommendation for anything not answered above, say what you assumed, and carry on with what this run is for (build it, when you were grilling before a build).';

/**
 * SF-T-308: whether a factory message's questions are a build's grilling, where Enough, build it fits: not a feedback
 * run's (it stays in feedback until someone asks for the build) or a conflict fix's decision.
 */
export function isBuildGrill(body: string): boolean {
  return !/stays in feedback until someone asks it to build|needs a decision to fix/.test(body);
}

/** SF-T-308: the line after answers with Backlog ones, for the agent that makes the tasks. */
export const BACKLOG_LINE =
  'For each one marked Backlog, make a task for later with `sf-agent tasks create` (Backlog column), from the question and what you know of the work, and leave it out of this build (or make none, when you judge it shouldn\'t be a task). End that turn\'s report with a `factory-backlog` block saying, for each, the task you made or why none (`"question"`: its number above).';

/**
 * The answers as the chat message the person posts (and the agent reads). SF-T-308: `enough` (Enough, build it)
 * sends the ones not answered as skipped, with a line telling the agent to stop asking and build.
 */
export function formatFactoryAnswers(questions: FactoryQuestion[], answers: Record<string, FactoryAnswer | undefined>, options: { enough?: boolean } = {}): string {
  const lines = ['**Answers**', ''];
  let backlog = false;
  questions.forEach((q, i) => {
    const a = answers[q.id];
    const other = a?.other.trim() ?? '';
    const parts = a?.skipped || a?.backlog ? [] : [...(a?.picked ?? []), ...(other ? [other] : [])];
    lines.push(`${i + 1}. ${q.header ? `**${q.header}:** ` : ''}${q.question.replace(/\n+/g, ' ')}`);
    let answer: string;
    if (a?.backlog) {
      backlog = true;
      answer = other ? `${BACKLOG_ANSWER}: ${other}` : BACKLOG_ANSWER;
    } else if (parts.length) answer = parts.join('; ');
    else answer = a?.skipped || options.enough ? SKIPPED_ANSWER : '_(no answer)_';
    // SF-T-265: an answer of several lines keeps them, indented under the arrow's text.
    lines.push(`   → ${indentFollowingLines(answer, ANSWER_INDENT)}`);
  });
  if (backlog) lines.push('', BACKLOG_LINE);
  if (options.enough) lines.push('', ENOUGH_LINE);
  return lines.join('\n');
}

/** SF-T-308: whether an answers message ends the grilling (Enough, build it). */
export const answersSayEnough = (message: string): boolean => message.includes(ENOUGH_LINE);

/**
 * T-22: reads back a message formatFactoryAnswers wrote, one answer per question in order, so the
 * task's Decisions can show each question with its answer. Null when the message isn't one.
 * An unanswered question reads as null, a skipped one as SKIPPED_ANSWER (SF-T-227).
 */
export function parseFactoryAnswers(message: string): (string | null)[] | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines[0]?.trim() !== '**Answers**') return null;
  const answers: (string | null)[] = [];
  for (let i = 1; i < lines.length; ) {
    const line = lines[i++];
    if (/^\d+\.\s/.test(line)) answers.push(null);
    else {
      const answer = /^\s+→\s?(.*)$/.exec(line);
      if (answer && answers.length && answers[answers.length - 1] === null) {
        const more = readFollowingLines(lines, i, ANSWER_INDENT);
        i = more.next;
        const text = [answer[1].trim(), more.text].filter(Boolean).join('\n');
        answers[answers.length - 1] = text && text !== '_(no answer)_' ? text : null;
      }
    }
  }
  return answers.length ? answers : null;
}

/**
 * SF-T-163: the first line of a reply to a round written in words (the card's "Reply in words"
 * button), in place of picking an answer per question. Like an Answers message, it closes the round
 * it replies to; a plain comment never does.
 */
const QUESTIONS_REPLY_HEAD = '**Reply to the questions**';

/**
 * The longest reply in words: what fits in a task comment (TASK_CHAT_MAX, the smaller of the two places
 * the card posts to; a chat message takes CHAT_MESSAGE_MAX_CHARS) with the heading, so the server never cuts it short.
 */
export const QUESTIONS_REPLY_MAX = TASK_CHAT_MAX - QUESTIONS_REPLY_HEAD.length - 2;

/** A reply in words to a round of questions, as the chat message the person posts (and the agent reads). */
export function formatQuestionsReply(text: string): string {
  return `${QUESTIONS_REPLY_HEAD}\n\n${text.trim()}`;
}

/** The text of a reply formatQuestionsReply wrote; null when the message isn't one. */
export function parseQuestionsReply(message: string): string | null {
  const body = message.replace(/\r\n?/g, '\n').trim();
  if (!body.startsWith(QUESTIONS_REPLY_HEAD)) return null;
  const rest = body.slice(QUESTIONS_REPLY_HEAD.length);
  if (rest && !rest.startsWith('\n')) return null;
  return rest.trim();
}
